import ExcelJS from "exceljs";

interface GameFormatRound {
  order: number;
  questionCount: number;
}

interface GameFormat {
  id: number;
  name: string;
  rounds: GameFormatRound[];
}

interface GameData {
  id: number;
  slug: string;
  name: string;
  format: GameFormat;
}

interface Team {
  key: number;
  name: string;
  teamId: number | null;
  answers: (boolean | null)[][];
}

interface DbTeamRef {
  id: number;
  name: string;
}

interface ImportResult {
  teams: Team[];
  warnings: string[];
}

const META_SHEET_NAME = "__3SUAL_META";

const normalize = (value: string): string =>
  value
    .toLocaleLowerCase("az")
    .trim()
    .replace(/\s+/g, " ");

const NON_TEAM_LABELS = new Set([
  "çətinlik",
  "cetinlik",
  "difficulty",
]);

const toInt = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === "string") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
  }
  if (value && typeof value === "object" && "result" in (value as any)) {
    return toInt((value as any).result);
  }
  return null;
};

const toText = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value && typeof value === "object") {
    const v = value as any;
    if (typeof v.text === "string") return v.text;
    if (v.richText && Array.isArray(v.richText)) {
      return v.richText.map((x: any) => x?.text ?? "").join("");
    }
    if ("result" in v) return toText(v.result);
  }
  return "";
};

const parseAnswer = (value: unknown): boolean | null => {
  if (value == null) return null;

  if (typeof value === "object" && value && "result" in (value as any)) {
    return parseAnswer((value as any).result);
  }

  if (typeof value === "number") {
    if (value === 1) return true;
    if (value === 0 || value === -1) return false;
    return null;
  }

  if (typeof value === "boolean") return value;

  const t = toText(value).trim().toLocaleLowerCase("az");
  if (!t) return null;

  if (["1", "+", "true", "yes", "y", "d", "doğru", "dogru"].includes(t)) return true;
  if (["0", "-", "x", "false", "no", "n", "yanlış", "yanlis"].includes(t)) return false;

  return null;
};

const getMeta = (workbook: ExcelJS.Workbook): Record<string, string> => {
  const sheet = workbook.getWorksheet(META_SHEET_NAME);
  if (!sheet) return {};

  const data: Record<string, string> = {};
  for (let r = 1; r <= sheet.rowCount; r++) {
    const key = toText(sheet.getRow(r).getCell(1).value).trim();
    const value = toText(sheet.getRow(r).getCell(2).value).trim();
    if (key) data[key] = value;
  }
  return data;
};

const buildEmptyAnswers = (rounds: GameFormatRound[]): (boolean | null)[][] =>
  rounds.map(r => Array(r.questionCount).fill(null));

export async function importFromExcel(
  file: File,
  game: GameData,
  existingTeams: Team[],
  allTeams: DbTeamRef[]
): Promise<ImportResult> {
  const workbook = new ExcelJS.Workbook();
  const arrayBuffer = await file.arrayBuffer();
  await workbook.xlsx.load(arrayBuffer);

  const warnings: string[] = [];
  const meta = getMeta(workbook);

  if (meta.slug && meta.slug !== game.slug) {
    throw new Error("Bu Excel başqa oyuna aiddir (slug uyğun gəlmir).");
  }

  if (meta.gameId) {
    const metaGameId = Number(meta.gameId);
    if (Number.isFinite(metaGameId) && metaGameId !== game.id) {
      throw new Error("Bu Excel başqa oyuna aiddir (gameId uyğun gəlmir).");
    }
  }

  if (meta.formatId) {
    const metaFormatId = Number(meta.formatId);
    if (Number.isFinite(metaFormatId) && metaFormatId !== game.format.id) {
      throw new Error("Bu Excel-in formatı cari oyunun formatına uyğun deyil.");
    }
  }

  if (meta.rounds) {
    try {
      const metaRounds = JSON.parse(meta.rounds) as number[];
      const currentRounds = game.format.rounds.map(r => r.questionCount);
      if (JSON.stringify(metaRounds) !== JSON.stringify(currentRounds)) {
        throw new Error("Bu Excel-in tur strukturu cari oyundan fərqlidir.");
      }
    } catch (err) {
      if (err instanceof Error && err.message.includes("fərqlidir")) throw err;
      warnings.push("Meta rounds məlumatı oxuna bilmədi, fayl fallback qaydada oxundu.");
    }
  }

  const existingByKey = new Map<number, Team>();
  existingTeams.forEach(t => existingByKey.set(t.key, t));

  const existingByName = new Map<string, Team[]>();
  existingTeams.forEach(t => {
    const key = normalize(t.name);
    if (!existingByName.has(key)) existingByName.set(key, []);
    existingByName.get(key)!.push(t);
  });

  const dbTeamByName = new Map<string, DbTeamRef[]>();
  allTeams.forEach(t => {
    const key = normalize(t.name);
    if (!dbTeamByName.has(key)) dbTeamByName.set(key, []);
    dbTeamByName.get(key)!.push(t);
  });

  const imported = new Map<number, Team>();
  const dynamicKeysByName = new Map<string, number>();
  let nextDynamicKey = Math.max(1, ...existingTeams.map(t => t.key)) + 1;

  for (let rIdx = 0; rIdx < game.format.rounds.length; rIdx++) {
    const nQ = game.format.rounds[rIdx].questionCount;
    const sheetName = `Tur ${rIdx + 1}`;
    const ws = workbook.getWorksheet(sheetName);

    if (!ws) {
      throw new Error(`${sheetName} vərəqi tapılmadı.`);
    }

    const header = ws.getRow(1);
    let keyColN = nQ + 5;

    for (let c = 1; c <= Math.max(ws.columnCount, keyColN); c++) {
      const title = toText(header.getCell(c).value).trim();
      if (title === "__KEY") {
        keyColN = c;
        break;
      }
    }

    for (let rowN = 2; rowN <= ws.rowCount; rowN++) {
      const row = ws.getRow(rowN);
      const teamName = toText(row.getCell(2).value).trim();
      const answers = Array.from({ length: nQ }, (_, qi) => parseAnswer(row.getCell(3 + qi).value));
      const hasAnyAnswer = answers.some(a => a !== null);
      const normalizedTeamName = normalize(teamName);

      const rawKey = toInt(row.getCell(keyColN).value);
      const hasAnyData = Boolean(teamName) || hasAnyAnswer || rawKey !== null;
      if (!hasAnyData) continue;
      if (NON_TEAM_LABELS.has(normalizedTeamName)) continue;

      const nameKey = normalizedTeamName;
      let resolvedKey: number | null = null;

      if (rawKey !== null) {
        resolvedKey = rawKey;
      } else if (nameKey && dynamicKeysByName.has(nameKey)) {
        resolvedKey = dynamicKeysByName.get(nameKey)!;
      } else if (nameKey && existingByName.has(nameKey) && (existingByName.get(nameKey)?.length ?? 0) === 1) {
        resolvedKey = existingByName.get(nameKey)![0].key;
      }

      if (resolvedKey === null) {
        resolvedKey = nextDynamicKey++;
      }

      if (!nameKey) {
        warnings.push(`${sheetName} / ${rowN}: Komanda adı boşdur, sətir ötürüldü.`);
        continue;
      }

      dynamicKeysByName.set(nameKey, resolvedKey);

      if (!imported.has(resolvedKey)) {
        const existing = existingByKey.get(resolvedKey);
        const dbMatchList = dbTeamByName.get(nameKey) ?? [];
        const dbMatch = dbMatchList.length === 1 ? dbMatchList[0] : null;

        imported.set(resolvedKey, {
          key: resolvedKey,
          name: teamName,
          teamId: existing?.teamId ?? dbMatch?.id ?? null,
          answers: existing ? existing.answers.map(round => [...round]) : buildEmptyAnswers(game.format.rounds),
        });

        if (!existing && dbMatchList.length > 1) {
          warnings.push(`${teamName}: DB-də eyni adda bir neçə komanda var, teamId avtomatik təyin edilmədi.`);
        }
      }

      const target = imported.get(resolvedKey)!;
      target.name = teamName;
      target.answers[rIdx] = answers;
    }
  }

  const teams = Array.from(imported.values());
  if (teams.length === 0) {
    throw new Error("Excel-dən heç bir komanda oxuna bilmədi.");
  }

  // Normalize missing rounds and answer widths.
  for (const team of teams) {
    for (let rIdx = 0; rIdx < game.format.rounds.length; rIdx++) {
      const nQ = game.format.rounds[rIdx].questionCount;
      const row = team.answers[rIdx] ?? [];
      if (row.length !== nQ) {
        team.answers[rIdx] = Array.from({ length: nQ }, (_, i) => row[i] ?? null);
      }
    }
  }

  return { teams, warnings };
}
